import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty.js';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import useModal from 'hooks/useModal.js';

export const DrawerContext = createContext({
  scrollDrawerContent: true,
});

export const useDrawerContext = () => useContext(DrawerContext);

/**
 * Only whether a drawer is open. The navigation shell and the players on the
 * page read this instead of the full context: a drawer that refreshes its own
 * pages changes the full context on every refresh, and re-rendering the shell
 * (framer `layout` measurements) and the players for that starves the page
 * when it is heavy -- a long show with many products and chat messages.
 * Undefined without a provider, like `drawerIsOpen` in the full context, so a
 * reader can tell "no drawer system" from "closed".
 */
export const DrawerIsOpenContext = createContext(undefined);

export const useDrawerIsOpen = () => useContext(DrawerIsOpenContext);

/**
 * Whether the page behind the open drawer is pushed back in 3D (the default)
 * or only dimmed. A drawer opts out through `openDrawer({ pushBackEffect:
 * false })`. Read by the navigation shell next to the open flag, for the same
 * reason that flag has its own context.
 */
export const DrawerPushBackEffectContext = createContext(true);

export const useDrawerPushBackEffect = () => useContext(DrawerPushBackEffectContext);

/**
 * True for everything rendered as a drawer's own content, and false elsewhere.
 * Set by the drawer, so a component can tell it sits inside one -- which
 * `drawerIsOpen` alone cannot say: it is false on every page with no drawer
 * open, and true for a player on the page beneath one that is.
 */
export const InsideDrawerContext = createContext(false);

export const useIsInsideDrawer = () => useContext(InsideDrawerContext);

export const DrawerContextProvider = ({ children }) => {
  const { features: { productsNavigationEnabled } } = useFeatureContext();
  const { isOpen: drawerIsOpen, open: openDrawerInState, close: closeDrawerInState } = useModal();
  const [isCloseOnOverlayActive, setIsCloseOnOverlayActive] = useState(true);
  const [pushBackEffect, setPushBackEffect] = useState(true);
  const [drawerPages, setDrawerPages] = useState([]);
  const [page, setPage] = useState(0);
  const [drawerContext, setDrawerContext] = useState(null);
  const [scrollDrawerContent, setScrollDrawerContent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [id, setId] = useState();

  const openDrawer = useCallback((options) => {
    const { drawerPages, isCloseOnOverlayActive = true, pushBackEffect = true, context, id } = options || {};
    setId(id);
    setDrawerPages(drawerPages);
    setPage(prevPage => drawerPages.length > prevPage ? prevPage : 0);
    openDrawerInState();
    setIsCloseOnOverlayActive(isCloseOnOverlayActive);
    setPushBackEffect(pushBackEffect);
    // A drawer re-opens with an empty context to refresh its pages. Keep the
    // existing object then: a copy with the same entries would re-render every
    // consumer for nothing. The first open still turns null into {} -- drawers
    // read a null context as "closed and removed".
    setDrawerContext(prev => (prev && isEmpty(context) ? prev : { ...prev, ...context }));
  }, [openDrawerInState]);

  const onClose = drawerPages[page]?.onClose;
  const closeDrawer = useCallback(() => {
    closeDrawerInState();
    if (typeof onClose !== 'function') return;
    onClose();
    setPage(0);
  }, [closeDrawerInState, onClose]);

  const hasDrawerPages = drawerPages?.length > 0;
  const removeDrawer = useCallback(() => {
    if (!hasDrawerPages) return;
    if (drawerIsOpen) closeDrawer();
    setId(null);
    setPage(0);
    setDrawerPages([]);
    setDrawerContext(null);
    setScrollDrawerContent(true);
    setPushBackEffect(true);
    // At this point we should no longer have any drawers to present,
    // hence purging this value seems to make sense.
  }, [hasDrawerPages, drawerIsOpen, closeDrawer]);

  const setPageAndUpdateState = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const context = useMemo(() => ({
    drawerPages,
    drawerContext,
    id,
    setDrawerPages,
    setDrawerContext,
    page,
    setPage: setPageAndUpdateState,
    drawerIsOpen,
    openDrawerInState,
    closeDrawerInState,
    removeDrawer,
    openDrawer,
    closeDrawer,
    isCloseOnOverlayActive,
    scrollDrawerContent,
    setScrollDrawerContent,
    isSubmitting,
    setIsSubmitting,
  }), [
    drawerPages,
    drawerContext,
    id,
    page,
    setPageAndUpdateState,
    drawerIsOpen,
    openDrawerInState,
    closeDrawerInState,
    removeDrawer,
    openDrawer,
    closeDrawer,
    isCloseOnOverlayActive,
    scrollDrawerContent,
    isSubmitting,
  ]);

  // To keep old dashboard UI keep using MUI Drawer we just return children
  if (!productsNavigationEnabled) return children;

  return (
    <DrawerContext.Provider value={context}>
      <DrawerIsOpenContext.Provider value={drawerIsOpen}>
        <DrawerPushBackEffectContext.Provider value={pushBackEffect}>
          {children}
        </DrawerPushBackEffectContext.Provider>
      </DrawerIsOpenContext.Provider>
    </DrawerContext.Provider>
  );
};
